import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Download, Search } from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { downloadCsv, rowsToCsv } from '@/lib/csvExport';

function isoDaysAgo(days: number): string {
  const d = new Date(); d.setDate(d.getDate() - days); d.setHours(0, 0, 0, 0);
  return d.toISOString();
}
function startOfDay(s: string) { const d = new Date(s); d.setHours(0, 0, 0, 0); return d.toISOString(); }
function endOfDay(s: string) { const d = new Date(s); d.setHours(23, 59, 59, 999); return d.toISOString(); }

interface SearchLog {
  id: string;
  query: string | null;
  filters: any;
  results_count: number | null;
  user_id: string | null;
  created_at: string;
}

export function SearchInsights() {
  const [range, setRange] = useState<'7' | '30' | '90' | 'custom'>('30');
  const [from, setFrom] = useState(() => new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  const fromIso = range === 'custom' ? startOfDay(from) : isoDaysAgo(parseInt(range, 10));
  const toIso = range === 'custom' ? endOfDay(to) : new Date().toISOString();

  const { data: logs, isLoading } = useQuery({
    queryKey: ['search-insights', range, from, to],
    queryFn: async () => {
      const { data } = await supabase.from('search_logs')
        .select('id, query, filters, results_count, user_id, created_at')
        .gte('created_at', fromIso).lte('created_at', toIso)
        .order('created_at', { ascending: false })
        .limit(5000);
      return (data || []) as SearchLog[];
    },
  });

  const kpis = useMemo(() => {
    const total = logs?.length ?? 0;
    const queries = (logs || []).map(l => (l.query || '').trim().toLowerCase()).filter(Boolean);
    const unique = new Set(queries).size;
    const zero = (logs || []).filter(l => (l.results_count ?? 0) === 0).length;
    const avg = total > 0
      ? Math.round(((logs || []).reduce((s, l) => s + (l.results_count ?? 0), 0) / total) * 10) / 10
      : 0;
    return {
      total, unique, zero,
      zeroRate: total > 0 ? Math.round((zero / total) * 1000) / 10 : 0,
      avg,
    };
  }, [logs]);

  const topQueries = useMemo(() => {
    const map: Record<string, { count: number; totalResults: number; zero: number; lastSeen: string }> = {};
    (logs || []).forEach(l => {
      const q = (l.query || '').trim().toLowerCase();
      if (!q) return;
      const m = map[q] || { count: 0, totalResults: 0, zero: 0, lastSeen: l.created_at };
      m.count += 1;
      m.totalResults += l.results_count ?? 0;
      if ((l.results_count ?? 0) === 0) m.zero += 1;
      if (l.created_at > m.lastSeen) m.lastSeen = l.created_at;
      map[q] = m;
    });
    return Object.entries(map)
      .map(([q, m]) => ({
        query: q,
        count: m.count,
        avg: Math.round((m.totalResults / m.count) * 10) / 10,
        zeroPct: Math.round((m.zero / m.count) * 1000) / 10,
        lastSeen: m.lastSeen,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);
  }, [logs]);

  const zeroQueries = useMemo(() =>
    topQueries.filter(q => q.zeroPct === 100).slice(0, 50),
  [topQueries]);

  const filterCombos = useMemo(() => {
    const map: Record<string, number> = {};
    (logs || []).forEach(l => {
      if (!l.filters || typeof l.filters !== 'object') return;
      const keys = Object.keys(l.filters).filter(k => l.filters[k] != null && l.filters[k] !== '');
      if (keys.length === 0) return;
      const sig = keys.sort().map(k => {
        const v = l.filters[k];
        return `${k}:${typeof v === 'object' ? JSON.stringify(v) : v}`;
      }).join(' · ');
      map[sig] = (map[sig] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 20)
      .map(([combo, count]) => ({ combo, count }));
  }, [logs]);

  const trend = useMemo(() => {
    const map: Record<string, { searches: number; zero: number }> = {};
    (logs || []).forEach(l => {
      const d = new Date(l.created_at).toISOString().slice(0, 10);
      const m = map[d] || { searches: 0, zero: 0 };
      m.searches += 1;
      if ((l.results_count ?? 0) === 0) m.zero += 1;
      map[d] = m;
    });
    return Object.keys(map).sort().map(d => ({
      day: d,
      searches: map[d].searches,
      zeroRate: map[d].searches > 0 ? Math.round((map[d].zero / map[d].searches) * 1000) / 10 : 0,
    }));
  }, [logs]);

  const exportTop = () => downloadCsv('top_queries', rowsToCsv(topQueries));
  const exportZero = () => downloadCsv('zero_result_queries', rowsToCsv(zeroQueries));
  const exportCombos = () => downloadCsv('filter_combos', rowsToCsv(filterCombos));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Search Insights</h1>
          <p className="text-sm text-muted-foreground">What users search, what we deliver, where we miss.</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Range</label>
            <Select value={range} onValueChange={(v: any) => setRange(v)}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {range === 'custom' && (
            <>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">From</label>
                <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="w-40" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">To</label>
                <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="w-40" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* KPI cards */}
      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total searches" value={kpis.total.toLocaleString()} />
          <KpiCard label="Unique queries" value={kpis.unique.toLocaleString()} />
          <KpiCard label="Zero-result rate" value={`${kpis.zeroRate}%`} tone={kpis.zeroRate > 25 ? 'warn' : 'ok'} />
          <KpiCard label="Avg results / search" value={String(kpis.avg)} />
        </div>
      )}

      {/* Trend */}
      <Card>
        <CardHeader><CardTitle className="text-base">Searches per day (with zero-result %)</CardTitle></CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="l" allowDecimals={false} />
                <YAxis yAxisId="r" orientation="right" unit="%" />
                <Tooltip />
                <Legend />
                <Line yAxisId="l" type="monotone" dataKey="searches" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                <Line yAxisId="r" type="monotone" dataKey="zeroRate" stroke="hsl(var(--destructive))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Top queries */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Search className="h-4 w-4" /> Top queries</CardTitle>
          <Button size="sm" variant="outline" onClick={exportTop}><Download className="h-4 w-4 mr-1.5" /> CSV</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Query</TableHead>
                <TableHead className="text-right">Count</TableHead>
                <TableHead className="text-right">Avg results</TableHead>
                <TableHead className="text-right">Zero %</TableHead>
                <TableHead>Last seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topQueries.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">No search activity in this range.</TableCell></TableRow>
              )}
              {topQueries.map(q => (
                <TableRow key={q.query}>
                  <TableCell className="font-medium">{q.query}</TableCell>
                  <TableCell className="text-right">{q.count}</TableCell>
                  <TableCell className="text-right">{q.avg}</TableCell>
                  <TableCell className="text-right">
                    {q.zeroPct > 50 ? <Badge variant="destructive">{q.zeroPct}%</Badge> : `${q.zeroPct}%`}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(q.lastSeen).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Zero-result queries */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Zero-result queries (inventory gaps)</CardTitle>
          <Button size="sm" variant="outline" onClick={exportZero}><Download className="h-4 w-4 mr-1.5" /> CSV</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Query</TableHead>
                <TableHead className="text-right">Times searched</TableHead>
                <TableHead>Last seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {zeroQueries.length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6">No queries returned zero results — nice.</TableCell></TableRow>
              ) : zeroQueries.map(q => (
                <TableRow key={q.query}>
                  <TableCell className="font-medium">{q.query}</TableCell>
                  <TableCell className="text-right">{q.count}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(q.lastSeen).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Filter combos */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Top filter combinations</CardTitle>
          <Button size="sm" variant="outline" onClick={exportCombos}><Download className="h-4 w-4 mr-1.5" /> CSV</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Filter combo</TableHead>
                <TableHead className="text-right">Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filterCombos.length === 0 ? (
                <TableRow><TableCell colSpan={2} className="text-center text-sm text-muted-foreground py-6">No filter usage in this range.</TableCell></TableRow>
              ) : filterCombos.map((c, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono text-xs">{c.combo}</TableCell>
                  <TableCell className="text-right">{c.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value, tone }: { label: string; value: string; tone?: 'ok' | 'warn' }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className={`text-2xl font-bold mt-1 ${tone === 'warn' ? 'text-destructive' : ''}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
